import { CssBaseline, ThemeProvider } from '@mui/material';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useRoutes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import CookieConsentBanner from '@components/CookieConsentBanner';
import EscapeWebViewBanner from '@components/EscapeWebViewBanner';
import MaintenancePage from '@components/Maintenance';

import { useVersionCheck } from '@hooks/useVersionCheck';
import { RTL } from '@layouts/full/shared/customizer/RTL';
import { useMemo } from 'react';
import './App.css';
import ScrollToTop from './components/shared/ScrollToTop';
import { MixpanelProvider } from './context/MixpanelProvider';
import { ThemeProvider as CustomThemeProvider } from './context/ThemeContext';
import { useHealthStatus } from './hooks/getQueries';
import { useThemeSettings } from './hooks/useThemeSettings';
import { Router } from './routes/Router';
import { buildTheme } from './theme/Theme';

function AppContent() {
  const themeSettings = useThemeSettings();
  const routing = useRoutes(Router);
  useVersionCheck();

  // Create theme based on theme settings
  const theme = useMemo(() => {
    return buildTheme({
      activeMode: themeSettings.activeMode,
      activeTheme: themeSettings.activeTheme,
      activeDir: themeSettings.activeDir,
      borderRadius: themeSettings.borderRadius,
      isLanguage: themeSettings.isLanguage,
    });
  }, [
    themeSettings.activeMode,
    themeSettings.activeTheme,
    themeSettings.activeDir,
    themeSettings.borderRadius,
    themeSettings.isLanguage,
  ]);

  // Set document direction based on theme
  document.dir = themeSettings.activeDir;

  const GOOGLE_OAUTH_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
  const { isError } = useHealthStatus();

  if (isError) {
    return <MaintenancePage />;
  }

  return (
    <ThemeProvider theme={theme}>
      <EscapeWebViewBanner>
        <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
          <MixpanelProvider>
            <ToastContainer position="top-right" autoClose={5000} pauseOnHover theme="light" />
            <RTL>
              <CssBaseline />
              <ScrollToTop>{routing}</ScrollToTop>
            </RTL>
            <CookieConsentBanner />
          </MixpanelProvider>
        </GoogleOAuthProvider>
      </EscapeWebViewBanner>
    </ThemeProvider>
  );
}

function App() {
  return (
    <CustomThemeProvider>
      <AppContent />
    </CustomThemeProvider>
  );
}

export default App;
